// The learner's progress: profile, XP/coins/streak, path stars, wardrobe and the
// word bank. Lives in memory + localStorage (instant, works offline) and syncs to
// the account (POST /account/state) so it follows the learner across devices.
//
// Sync is optimistic: every change is pushed (debounced) with the version we last
// saw. If another device wrote in between, the server answers 409 with its copy;
// we merge (per section last-writer-wins, per-word for the word bank, unions for
// owned items, max for lesson stars) and push again.

import { useSyncExternalStore } from "react";
import { post, ApiError } from "./api";
import { DEFAULT_PROFILE, type LearnerProfile, type ShopItem, type CollectionSet } from "./types";
import { newCard, review, type Grade, type VocabCard } from "./srs";

export const WELCOME_COINS = 100;

export interface Outfit { hat: string | null; face: string | null; neck: string; held: string | null; halo: string }
export const DEFAULT_OUTFIT: Outfit = { hat: null, face: null, neck: "neck.firuze", held: null, halo: "halo.firuze" };

export interface LearnState {
  v: 1;
  profile: LearnerProfile;
  profileAt: number;
  xp: number;
  coins: number;
  streak: number;
  lastDay: string; // local yyyy-mm-dd of the last practice
  progressAt: number;
  path: Record<string, number>; // lesson id → best stars (1-3)
  owned: string[];
  outfit: Outfit;
  claimedSets: string[];
  wardrobeAt: number;
  words: Record<string, VocabCard>; // keyed by the Persian text
  recentStories: string[];
  /** Local practice log for the activity heatmap: yyyy-mm-dd → XP earned that day. */
  days: Record<string, number>;
}

export function freshState(): LearnState {
  return {
    v: 1,
    profile: { ...DEFAULT_PROFILE },
    profileAt: 0,
    xp: 0,
    coins: WELCOME_COINS,
    streak: 0,
    lastDay: "",
    progressAt: 0,
    path: {},
    owned: ["neck.firuze", "halo.firuze"],
    outfit: { ...DEFAULT_OUTFIT },
    claimedSets: [],
    wardrobeAt: 0,
    words: {},
    recentStories: [],
    days: {},
  };
}

/** Tolerant load: unknown keys dropped, missing keys defaulted. An update never wipes progress. */
export function normalize(raw: unknown): LearnState {
  const base = freshState();
  if (!raw || typeof raw !== "object") return base;
  const r = raw as Partial<LearnState>;
  return {
    ...base,
    ...r,
    v: 1,
    profile: { ...base.profile, ...(r.profile || {}) },
    outfit: { ...base.outfit, ...(r.outfit || {}) },
    path: { ...(r.path || {}) },
    words: { ...(r.words || {}) },
    days: { ...(r.days || {}) },
    owned: Array.isArray(r.owned) ? r.owned : base.owned,
    claimedSets: Array.isArray(r.claimedSets) ? r.claimedSets : [],
    recentStories: Array.isArray(r.recentStories) ? r.recentStories : [],
  };
}

/** Merge two copies of the state (local edits + another device's). Pure. */
export function merge(a: LearnState, b: LearnState): LearnState {
  const pick = <K extends keyof LearnState>(at: keyof LearnState, keys: K[]) => {
    const src = (b[at] as number) > (a[at] as number) ? b : a;
    return Object.fromEntries(keys.map((k) => [k, src[k]])) as Pick<LearnState, K>;
  };
  const words: Record<string, VocabCard> = { ...a.words };
  for (const [k, w] of Object.entries(b.words)) if (!words[k] || w.updatedAt > words[k].updatedAt) words[k] = w;
  const path: Record<string, number> = { ...a.path };
  for (const [k, s] of Object.entries(b.path)) path[k] = Math.max(path[k] || 0, s);
  const days: Record<string, number> = { ...a.days };
  for (const [k, n] of Object.entries(b.days)) days[k] = Math.max(days[k] || 0, n);
  const progress = pick("progressAt", ["xp", "coins", "streak", "lastDay", "progressAt"]);
  return {
    ...a,
    ...pick("profileAt", ["profile", "profileAt"]),
    ...progress,
    xp: Math.max(a.xp, b.xp), // XP only grows: never lose points to a race
    ...pick("wardrobeAt", ["outfit", "wardrobeAt"]),
    owned: [...new Set([...a.owned, ...b.owned])],
    claimedSets: [...new Set([...a.claimedSets, ...b.claimedSets])],
    path,
    words,
    days,
    recentStories: [...new Set([...a.recentStories, ...b.recentStories])].slice(0, 20),
  };
}

// ---- the store ----

let state: LearnState = freshState();
let userId: string | null = null;
let version = 0; // server stateVersion we last saw
const listeners = new Set<() => void>();
const storageKey = () => `pashmak.state.${userId}`;

function emit() {
  listeners.forEach((f) => f());
}

function persistLocal() {
  if (!userId) return;
  try {
    localStorage.setItem(storageKey(), JSON.stringify({ state, version, dirty }));
  } catch {
    /* storage full or blocked: the server copy still syncs */
  }
}

export function getState() {
  return state;
}

/** Subscribe a component to a slice of the state. */
export function useLearn<T>(select: (s: LearnState) => T): T {
  return useSyncExternalStore(
    (f) => (listeners.add(f), () => void listeners.delete(f)),
    () => select(state),
    () => select(state)
  );
}

function set(next: LearnState) {
  state = next;
  dirty = true;
  persistLocal();
  emit();
  scheduleSync();
}

/**
 * Called after sign-in / on boot with what the server has. Local unsynced edits
 * (e.g. made offline) are merged in rather than thrown away.
 */
export function hydrate(id: string, serverState: unknown, serverVersion: number) {
  userId = id;
  let local: { state?: unknown; version?: number; dirty?: boolean } = {};
  try {
    local = JSON.parse(localStorage.getItem(storageKey()) || "{}");
  } catch {
    /* ignore */
  }
  const remote = serverState ? normalize(serverState) : null;
  const mine = local.state ? normalize(local.state) : null;
  version = serverVersion;
  if (remote && mine && local.dirty) {
    state = merge(mine, remote);
    dirty = true;
  } else if (remote) {
    state = remote;
    dirty = false;
  } else {
    state = mine || freshState();
    dirty = true; // first sign-in: push the starting state
  }
  persistLocal();
  emit();
  if (dirty) scheduleSync(0);
}

export function signOutState() {
  try {
    if (userId) localStorage.removeItem(storageKey());
  } catch {
    /* ignore */
  }
  userId = null;
  state = freshState();
  version = 0;
  dirty = false;
  emit();
}

// ---- sync ----

let dirty = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let inflight: Promise<void> | null = null;
type SyncStatus = "idle" | "saving" | "offline" | "error";
let syncStatus: SyncStatus = "idle";
const syncListeners = new Set<() => void>();
export function useSyncStatus() {
  return useSyncExternalStore(
    (f) => (syncListeners.add(f), () => void syncListeners.delete(f)),
    () => syncStatus
  );
}
function setSync(s: SyncStatus) {
  syncStatus = s;
  syncListeners.forEach((f) => f());
}

function scheduleSync(delay = 1500) {
  if (!userId) return;
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => void flush(), delay);
}

/** Push local changes now (also called on tab hide / before sign-out). */
export async function flush(): Promise<void> {
  if (inflight) return inflight;
  if (!dirty || !userId) return;
  inflight = (async () => {
    setSync("saving");
    for (let attempt = 0; attempt < 4; attempt++) {
      const sending = state;
      try {
        const r = await post<{ stateVersion: number }>("/account/state", { state: sending, baseVersion: version });
        version = r.stateVersion;
        if (state === sending) dirty = false; // nothing changed while we were saving
        persistLocal();
        setSync("idle");
        if (dirty) scheduleSync();
        return;
      } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
          const b = e.body as { state: unknown; stateVersion: number };
          version = b.stateVersion;
          state = merge(state, normalize(b.state));
          persistLocal();
          emit();
          continue;
        }
        setSync(e instanceof ApiError && e.status === 0 ? "offline" : "error");
        scheduleSync(15_000); // retry later
        return;
      }
    }
    setSync("error");
  })().finally(() => {
    inflight = null;
  });
  return inflight;
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => scheduleSync(0));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flush();
  });
}

// ---- actions ----

const now = () => Date.now();
export const localDay = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function updateProfile(change: Partial<LearnerProfile>) {
  set({ ...state, profile: { ...state.profile, ...change }, profileAt: now() });
}

/** Every XP point also earns a coin; the first activity each day extends the streak + bonus coins. */
export function award(points: number, at = new Date()): { streakUp: boolean; streak: number } {
  const today = localDay(at);
  const y = new Date(at);
  y.setDate(y.getDate() - 1);
  let { streak, coins } = state;
  let streakUp = false;
  if (today !== state.lastDay) {
    streak = state.lastDay === localDay(y) ? streak + 1 : 1;
    coins += 10 * Math.min(streak, 7);
    streakUp = true;
  }
  set({
    ...state,
    xp: state.xp + points,
    coins: coins + points,
    streak,
    lastDay: today,
    progressAt: now(),
    days: { ...state.days, [today]: (state.days[today] || 0) + points },
  });
  return { streakUp, streak };
}

/** Streak as shown today: a missed day means it's broken (until the next practice). */
export function liveStreak(s: LearnState, at = new Date()): number {
  const y = new Date(at);
  y.setDate(y.getDate() - 1);
  return s.lastDay === localDay(at) || s.lastDay === localDay(y) ? s.streak : 0;
}

export function addWord(w: { fa: string; finglish: string; en: string; note?: string | null }, source: string): boolean {
  const key = w.fa.trim();
  if (!key) return false;
  const cur = state.words[key];
  if (cur && !cur.deleted) return false; // saving twice keeps the first (and its history)
  set({ ...state, words: { ...state.words, [key]: newCard(w, source) } });
  return true;
}

export function hasWord(fa: string) {
  const c = state.words[fa.trim()];
  return !!c && !c.deleted;
}

export function reviewWord(fa: string, grade: Grade) {
  const c = state.words[fa];
  if (!c) return;
  set({ ...state, words: { ...state.words, [fa]: review(c, grade) } });
}

export function deleteWord(fa: string) {
  const c = state.words[fa];
  if (!c) return;
  set({ ...state, words: { ...state.words, [fa]: { ...c, deleted: true, updatedAt: now() } } });
}

export function completeLesson(id: string, stars: number) {
  set({ ...state, path: { ...state.path, [id]: Math.max(state.path[id] || 0, stars) } });
}

/** Mark many lessons done at once (the placement check) with at least `stars`, never lowering any. */
export function completeLessons(ids: string[], stars = 1) {
  const path = { ...state.path };
  for (const id of ids) path[id] = Math.max(path[id] || 0, stars);
  set({ ...state, path });
}

export function rememberStory(title: string) {
  set({ ...state, recentStories: [title, ...state.recentStories.filter((t) => t !== title)].slice(0, 20) });
}

// ---- wardrobe (Pashmak's Bazaar) ----

export const owns = (s: LearnState, id: string) => s.owned.includes(id);
export const wearing = (s: LearnState, item: ShopItem) => s.outfit[item.slot] === item.id;

function withWorn(o: Outfit, slot: ShopItem["slot"], id: string | null): Outfit {
  if (slot === "neck") return { ...o, neck: id ?? "neck.firuze" };
  if (slot === "halo") return { ...o, halo: id ?? "halo.firuze" };
  return { ...o, [slot]: id };
}

export type BuyResult = "bought" | "owned" | "poor";
export function buy(item: ShopItem): BuyResult {
  if (owns(state, item.id)) return "owned";
  if (state.coins < item.price) return "poor";
  set({
    ...state,
    coins: state.coins - item.price,
    progressAt: now(),
    owned: [...state.owned, item.id],
    outfit: withWorn(state.outfit, item.slot, item.id),
    wardrobeAt: now(),
  });
  return "bought";
}

export function grant(item: ShopItem) {
  set({ ...state, owned: [...new Set([...state.owned, item.id])], outfit: withWorn(state.outfit, item.slot, item.id), wardrobeAt: now() });
}

export function toggleWear(item: ShopItem) {
  if (!owns(state, item.id)) return;
  set({ ...state, outfit: withWorn(state.outfit, item.slot, wearing(state, item) ? null : item.id), wardrobeAt: now() });
}

/** Claim the bonus for any collection completed for the first time. Returns the set, if any. */
export function claimCompletedSets(sets: CollectionSet[], shop: ShopItem[]): CollectionSet | null {
  for (const set_ of sets) {
    if (state.claimedSets.includes(set_.id)) continue;
    const items = shop.filter((i) => i.set === set_.id);
    if (items.length && items.every((i) => state.owned.includes(i.id))) {
      set({ ...state, claimedSets: [...state.claimedSets, set_.id], coins: state.coins + set_.bonus, progressAt: now() });
      return set_;
    }
  }
  return null;
}

/** Test hook. */
export function _setForTest(s: LearnState, id = "test") {
  userId = id;
  state = s;
  version = 0;
  dirty = false;
  emit();
}
